import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaAmazon, FaFire, FaSnowflake, FaPepperHot, FaRadiation, FaThermometerHalf, FaLink, FaRobot, FaClipboardList } from 'react-icons/fa';
import { analyzeProduct, getRoastByAsin } from '../lib/analyzerService';
import { logger } from '../lib/loggerService';
import RoastCountdown from './RoastCountdown';
import LoadingScreen from './LoadingScreen';

interface FormState {
  input: string;
  roastLevel: 'mild' | 'medium' | 'spicy' | 'nuclear';
  marketplace: string;
  isLoading: boolean;
  error: string | null;
  showCountdown: boolean;
  countdownData: {
    roastLevel: string;
    createdAt: string;
  } | null;
}

const RoastForm: React.FC = () => {
  const [formState, setFormState] = React.useState<FormState>({
    input: '',
    roastLevel: 'medium',
    marketplace: 'com',
    isLoading: false,
    error: null,
    showCountdown: false,
    countdownData: null,
  });
  const navigate = useNavigate();

  // Function to extract ASIN from Amazon URL
  const extractAsinFromUrl = (url: string): string | null => {
    // Match ASIN pattern in URL
    const asinPattern = /(?:\/dp\/|\/gp\/product\/|\/ASIN\/|\/asin\/)([A-Z0-9]{10})/i;
    const match = url.match(asinPattern);
    
    return match ? match[1] : null;
  };

  // Function to validate if string is a valid ASIN
  const isValidAsin = (str: string): boolean => {
    return /^[A-Z0-9]{10}$/i.test(str);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formState.isLoading) {
      return;
    }
    
    let asin = formState.input.trim();
    let url = asin;
    
    try {
      // Check if input is a URL
      if (asin.startsWith('http')) {
        const extractedAsin = extractAsinFromUrl(asin);
        if (extractedAsin) {
          asin = extractedAsin;
        } else {
          throw new Error("Couldn't extract a valid ASIN from the URL. Please enter a valid Amazon product URL or ASIN.");
        }
      } else if (isValidAsin(asin)) {
        // If it's just an ASIN, convert to a URL for the API
        url = `https://www.amazon.com/dp/${asin}`;
      } else {
        throw new Error("Please enter a valid 10-character ASIN or Amazon product URL.");
      }
      
      // First check if the product already exists in the database
      const existingProduct = await getRoastByAsin(asin);
      
      if (existingProduct && existingProduct.created_at) {
        // Product exists in the database
        logger.log('Product already exists in database:', existingProduct);
        
        // Calculate if it's been less than 24 hours since creation
        const creationTime = new Date(existingProduct.created_at).getTime();
        const currentTime = new Date().getTime();
        const timeDifference = currentTime - creationTime;
        const hoursDifference = timeDifference / (1000 * 60 * 60);
        
        if (hoursDifference < 24) {
          // Show countdown without showing loading screen
          setFormState(prev => ({
            ...prev,
            showCountdown: true,
            countdownData: {
              roastLevel: existingProduct.analysis.roast_level,
              createdAt: existingProduct.created_at || new Date().toISOString()
            }
          }));
          
          // Store the result for viewing
          localStorage.setItem('lastRoastResult', JSON.stringify(existingProduct));
          return;
        }
        
        // If it's older than 24 hours, we'll re-analyze it
        // Fall through to the analysis code below
      }
      
      // If we reached here, we need to analyze the product
      // Show loading screen now
      setFormState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Call the analyzer service with roast level and marketplace
      const result = await analyzeProduct(url, formState.roastLevel, formState.marketplace);
      logger.log('Analysis result:', result);
      
      if (result && result.asin) {
        // Store the full analysis result in localStorage
        localStorage.setItem('lastRoastResult', JSON.stringify(result));
        navigate(`/results/${result.asin}`);
      } else {
        throw new Error('Invalid response from analysis server');
      }
    } catch (error: any) {
      logger.error('Analysis error:', error);
      setFormState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || "An error occurred while processing your request. Please try again."
      }));
    }
  };

  // Function to get color for roast level
  const getRoastLevelColor = (level: string): string => {
    switch (level) {
      case 'mild': return '#60a5fa'; // Light blue
      case 'medium': return '#f97316'; // Orange
      case 'spicy': return '#ef4444'; // Red
      case 'nuclear': return '#7c2d12'; // Dark brown
      default: return '#f97316';
    }
  };

  // Function to get icon for roast level
  const getRoastLevelIcon = (level: string) => {
    switch (level) {
      case 'mild': return <FaSnowflake style={{ color: '#60a5fa' }} />;
      case 'medium': return <FaThermometerHalf style={{ color: '#f97316' }} />;
      case 'spicy': return <FaPepperHot style={{ color: '#ef4444' }} />;
      case 'nuclear': return <FaRadiation style={{ color: '#7c2d12' }} />;
      default: return <FaThermometerHalf style={{ color: '#f97316' }} />;
    }
  };

  const handleLoadingClose = () => {
    setFormState(prev => ({ ...prev, isLoading: false, error: null }));
  };

  return (
    <>
      {formState.isLoading && (
        <LoadingScreen 
          onClose={handleLoadingClose} 
          listingUrl={formState.input}
          errorMessage={formState.error}
        />
      )}
      <div>
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)',
          color: 'white',
          padding: '10px 0 30px',
          textAlign: 'center'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 20px',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '5px'
            }}>
              <div 
                onClick={() => navigate('/')}
                style={{ 
                  cursor: 'pointer',
                  display: 'inline-block'
                }}
              >
                <img 
                  src="/logo.png" 
                  alt="RoastMyListing.ai" 
                  style={{
                    maxWidth: '500px',
                    width: '100%',
                    height: 'auto'
                  }}
                />
              </div>
            </div>
            <div style={{
              fontSize: '0.95rem',
              color: '#f8fafc',
              padding: '12px 16px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              marginTop: '15px',
              maxWidth: '800px',
              margin: '15px auto 0'
            }}>
              <strong>Note:</strong> This is a parody tool that delivers real insights with humor. 
              All roasts are AI-generated using actual e-commerce best practices.
            </div>
          </div>
        </div>
        
        {/* Form Container */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px',
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            padding: '25px',
            maxWidth: '550px',
            margin: '-20px auto 30px',
            position: 'relative',
            zIndex: 10,
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '20px',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              color: '#333'
            }}>
              <FaAmazon style={{ color: '#ff9900' }} /> Paste Amazon Link or ASIN
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div style={{ position: 'relative', marginBottom: '24px' }}>
                <input
                  type="text"
                  value={formState.input}
                  onChange={(e) => setFormState(prev => ({ ...prev, input: e.target.value }))}
                  placeholder="Paste Amazon URL or ASIN (we're not picky!)"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    border: '2px solid #ff5722',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    backgroundColor: 'rgba(255, 87, 34, 0.03)',
                    boxShadow: '0 0 0 3px rgba(255, 87, 34, 0.1)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ff7043';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 87, 34, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#ff5722';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 87, 34, 0.1)';
                  }}
                />
              </div>
              
              {/* Roast Level Selector */}
              <div style={{ marginBottom: '28px' }}>
                <label 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    fontWeight: '500',
                    color: '#4b5563'
                  }}
                >
                  <FaThermometerHalf /> Select Roast Level:
                </label>
                
                <div style={{ 
                  display: 'flex', 
                  gap: '10px',
                  flexWrap: 'wrap'
                }}>
                  {['mild', 'medium', 'spicy', 'nuclear'].map((level) => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setFormState(prev => ({ ...prev, roastLevel: level as any }))}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: formState.roastLevel === level ? '2px solid' : '1px solid #e5e7eb',
                        borderColor: formState.roastLevel === level ? getRoastLevelColor(level) : '#e5e7eb',
                        backgroundColor: formState.roastLevel === level ? `${getRoastLevelColor(level)}10` : 'white',
                        color: formState.roastLevel === level ? getRoastLevelColor(level) : '#4b5563',
                        fontWeight: formState.roastLevel === level ? 'bold' : 'normal',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textTransform: 'capitalize',
                        flex: '1 1 auto',
                        minWidth: '80px',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      {getRoastLevelIcon(level)} {level}
                    </button>
                  ))}
                </div>
                
                <p style={{ 
                  fontSize: '13px', 
                  color: '#6b7280', 
                  marginTop: '8px',
                  fontStyle: 'italic'
                }}>
                  {formState.roastLevel === 'mild' && "Gentle critique with constructive feedback."}
                  {formState.roastLevel === 'medium' && "Balanced critique with some humor and honesty."}
                  {formState.roastLevel === 'spicy' && "Brutally honest with sharp humor and criticism."}
                  {formState.roastLevel === 'nuclear' && "No mercy, maximum roasting intensity!"}
                </p>
              </div>
              
              {/* Marketplace Selector */}
              <div style={{ marginBottom: '28px' }}>
                <label 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    fontWeight: '500',
                    color: '#4b5563'
                  }}
                >
                  <FaAmazon style={{ color: '#ff9900' }} /> Select Marketplace:
                </label>
                
                <select
                  value={formState.marketplace}
                  onChange={(e) => setFormState(prev => ({ ...prev, marketplace: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid #3b82f6',
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: 'rgba(59, 130, 246, 0.05)',
                    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                    cursor: 'pointer'
                  }}
                >
                  <option value="com">Amazon.com (US)</option>
                  <option value="co.uk">Amazon.co.uk (UK)</option>
                  <option value="ca">Amazon.ca (Canada)</option>
                  <option value="com.au">Amazon.com.au (Australia)</option>
                </select>
              </div>
              
              {formState.error && (
                <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px' }}>
                  {formState.error}
                </p>
              )}
              
              <button
                type="submit"
                disabled={formState.isLoading}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: formState.isLoading ? '#9ca3af' : '#ff5722',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: formState.isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
                onMouseOver={(e) => {
                  if (!formState.isLoading) {
                    e.currentTarget.style.backgroundColor = '#ff7043';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 8px rgba(0, 0, 0, 0.15)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!formState.isLoading) {
                    e.currentTarget.style.backgroundColor = '#ff5722';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                {formState.isLoading ? (
                  <>
                    <span className="loading-spinner" style={{
                      display: 'inline-block',
                      width: '20px',
                      height: '20px',
                      border: '3px solid rgba(255,255,255,.3)',
                      borderRadius: '50%',
                      borderTopColor: 'white',
                      animation: 'spin 1s ease-in-out infinite',
                    }}></span>
                    Analyzing...
                  </>
                ) : (
                  <>
                    <FaFire /> Roast This Listing
                  </>
                )}
              </button>
              
              <style>
                {`
                  @keyframes spin {
                    to { transform: rotate(360deg); }
                  }
                `}
              </style>
            </form>
            
            <p style={{ 
              fontSize: '14px', 
              color: '#6b7280', 
              marginTop: '16px', 
              textAlign: 'center' 
            }}>
              Free analysis. No account required. Results in seconds.
            </p>
          </div>
          
          {/* Testimonial Quote - Parody */}
          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            borderRadius: '12px',
            padding: '15px 20px',
            maxWidth: '600px',
            margin: '0 auto 40px',
            position: 'relative',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
            background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.8) 0%, rgba(59, 130, 246, 0.8) 100%)',
          }}>
            <p style={{
              fontSize: '17px',
              fontStyle: 'italic',
              color: '#ffffff',
              margin: '0',
              position: 'relative',
              paddingLeft: '25px',
              fontWeight: '500',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
              lineHeight: '1.5'
            }}>
              <span style={{ 
                position: 'absolute',
                left: '0',
                top: '-2px',
                fontSize: '24px',
                color: '#FFD700'
              }}>❝</span>
              This tool roasted my listing so hard it needed aloe vera! Sales up 37% after fixes.
              <span style={{ 
                display: 'block', 
                fontSize: '14px', 
                marginTop: '8px', 
                fontWeight: 'normal',
                textAlign: 'right',
                paddingRight: '25px',
                position: 'relative',
                color: 'rgba(255, 255, 255, 0.9)'
              }}>
                — Jeff B., Totally Not the Amazon CEO
                <span style={{ 
                  position: 'absolute',
                  right: '0',
                  bottom: '-2px',
                  fontSize: '24px',
                  color: '#FFD700'
                }}>❞</span>
              </span>
            </p>
            <div style={{
              position: 'absolute',
              bottom: '-10px',
              right: '25px',
              backgroundColor: '#FFD700',
              borderRadius: '4px',
              padding: '3px 10px',
              fontSize: '11px',
              color: '#333',
              fontWeight: 'bold',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
              letterSpacing: '0.5px'
            }}>
              PARODY
            </div>
          </div>
        </div>
        
        {/* How It Works Section */}
        <div style={{
          backgroundColor: '#f8fafc', 
          padding: '60px 0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.04,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%234f46e5' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }} />
          
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 20px',
            position: 'relative',
            zIndex: 1
          }}>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              marginBottom: '40px',
              textAlign: 'center',
              color: '#1e293b',
              position: 'relative',
              display: 'inline-block',
              left: '50%',
              transform: 'translateX(-50%)'
            }}>
              <span style={{
                position: 'relative',
                zIndex: 1
              }}>How RoastMyListing.ai Works</span>
              <span style={{
                position: 'absolute',
                height: '8px',
                width: '100%',
                background: 'linear-gradient(90deg, rgba(255,87,34,0.7) 0%, rgba(255,87,34,0.3) 100%)',
                bottom: '0px',
                left: '0',
                zIndex: 0,
                borderRadius: '4px'
              }}></span>
            </h2>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '30px'
            }}>
              {[
                {
                  icon: <FaLink style={{ fontSize: '24px', color: '#ff5722' }} />,
                  title: 'Enter Your ASIN',
                  description: 'Paste your Amazon product ASIN and choose how badly you want us to hurt your feelings. Mild for a gentle nudge, Nuclear for full emotional damage.'
                },
                {
                  icon: <FaRobot style={{ fontSize: '24px', color: '#ff5722' }} />,
                  title: 'AI Does Its Magic',
                  description: 'Our AI reviews your listing like a cranky shopper who\'s had too much coffee and not enough sleep. It\'s like having Simon Cowell judge your product page.'
                },
                {
                  icon: <FaClipboardList style={{ fontSize: '24px', color: '#ff5722' }} />,
                  title: 'Laugh, Cry, Improve',
                  description: 'Get a hilariously brutal assessment that might make you question your life choices, along with suggestions that could actually help. Tissues not included.'
                }
              ].map((step, index) => (
                <div key={index} style={{
                  flex: '1 1 300px',
                  maxWidth: '350px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '30px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 87, 34, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    {step.icon}
                  </div>
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    left: '15px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#ff5722',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    boxShadow: '0 2px 5px rgba(255, 87, 34, 0.3)',
                    zIndex: 2
                  }}>
                    {index + 1}
                  </div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    marginBottom: '12px',
                    color: '#1e293b'
                  }}>
                    {step.title}
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    color: '#64748b',
                    lineHeight: '1.5'
                  }}>
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Benefits Section */}
        <div style={{
          padding: '60px 0',
          backgroundColor: '#f8fafc', /* Light gray background to match How It Works section */
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.04,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%234f46e5' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          }} />
          
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 20px',
            position: 'relative',
            zIndex: 1
          }}>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              marginBottom: '50px',
              textAlign: 'center',
              color: '#1e293b',
              position: 'relative',
              display: 'inline-block',
              left: '50%',
              transform: 'translateX(-50%)'
            }}>
              <span style={{
                position: 'relative',
                zIndex: 1
              }}>Benefits of RoastMyListing.ai</span>
              <span style={{
                position: 'absolute',
                height: '8px',
                width: '100%',
                background: 'linear-gradient(90deg, rgba(255,87,34,0.7) 0%, rgba(255,87,34,0.3) 100%)',
                bottom: '0px',
                left: '0',
                zIndex: 0,
                borderRadius: '4px'
              }}></span>
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '30px',
              marginBottom: '40px'
            }}>
              {[
                {
                  title: 'Data-Driven Inspiration',
                  description: 'Our AI analyzes successful Amazon listings to provide creative ideas that might spark your own listing improvements.',
                  icon: '📊'
                },
                {
                  title: 'Entertaining Analysis',
                  description: 'Get feedback with a sense of humor instead of boring consultant-speak. It\'s like having a comedy writer review your product page.',
                  icon: '⏱️'
                },
                {
                  title: 'Fresh Perspectives',
                  description: 'See your listing through new eyes and discover potential improvements you might have overlooked.',
                  icon: '📈'
                },
                {
                  title: 'Keyword Suggestions',
                  description: 'Get ideas for search terms that could help your listing appear in more relevant searches.',
                  icon: '🔍'
                },
                {
                  title: 'Honest Feedback',
                  description: 'Get brutally honest analysis that doesn\'t sugarcoat. Sometimes a fresh perspective hurts, but it can inspire great ideas!',
                  icon: '💬'
                },
                {
                  title: 'Creative Edge',
                  description: 'Find unique ways to present your product that might help it stand out from similar listings.',
                  icon: '🏆'
                }
              ].map((benefit, index) => (
                <div key={index} style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '30px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  border: '1px solid rgba(0, 0, 0, 0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                }}>
                  <div style={{
                    fontSize: '36px',
                    marginBottom: '15px',
                  }}>
                    {benefit.icon}
                  </div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    marginBottom: '12px',
                    color: '#1e293b'
                  }}>
                    {benefit.title}
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    color: '#64748b',
                    lineHeight: '1.6',
                    flex: '1'
                  }}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
            
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '30px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.03)',
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                marginBottom: '15px',
                color: '#1e293b'
              }}>
                RoastMyListing.ai
              </h3>
              <p style={{
                fontSize: '1.05rem',
                color: '#64748b',
                lineHeight: '1.6',
                marginBottom: '20px'
              }}>
                RoastMyListing.ai helps Amazon sellers optimize their product listings with data-driven insights and proven strategies.
              </p>
            </div>
          </div>
        </div>
        
        {/* Countdown Modal */}
        {formState.showCountdown && formState.countdownData && (
          <RoastCountdown
            roastLevel={formState.countdownData.roastLevel}
            createdAt={formState.countdownData.createdAt}
            onClose={() => {
              setFormState(prev => ({
                ...prev,
                showCountdown: false,
                countdownData: null
              }));
            }}
            onLoadingAnyway={() => {
              // Navigate to results page when "Load Anyway" is clicked
              const lastResult = localStorage.getItem('lastRoastResult');
              if (lastResult) {
                const result = JSON.parse(lastResult);
                navigate(`/results/${result.asin}`);
              }
              
              setFormState(prev => ({
                ...prev,
                showCountdown: false,
                countdownData: null
              }));
            }}
          />
        )}
      </div>
    </>
  );
};

export default RoastForm;
